/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

/**
 * Clips API Client
 *
 * Handles clip-related operations including fetching commits that reference clips.
 * Automatically injects Supabase JWT tokens for authentication.
 */

import { supabase } from '@/lib/supabase'

// =============================================
// TYPES (matching backend schemas)
// =============================================

export interface CommitInfo {
  id: string
  sequence_id: string
  sequence_name: string
  commit_number: number
  message: string
  author_name?: string
  created_at: string
}

export interface ClipCommitAssociation {
  id: string  // commit_clips.id
  commit_id: string
  clip_id: string

  // Clip state at commit time
  timeline_position_frames?: number
  source_in_frames?: number
  source_out_frames?: number
  duration_frames?: number
  track_name?: string

  // Timestamps
  created_at: string

  // Include commit details
  commit: CommitInfo
}

export interface ClipCommitsResponse {
  clip_id: string
  clip_name: string
  associations: ClipCommitAssociation[]
  total: number
}

export interface ClipAssignRequest {
  clip_ids: string[]
  assigned_user_id: string | null
}

export interface ClipResponse {
  id: string
  project_id: string
  name: string
  tape_name?: string
  assigned_user_id?: string
  assigned_at?: string
  created_at: string
  updated_at: string
}

// =============================================
// API CLIENT CONFIGURATION
// =============================================

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

/**
 * Get the current Supabase JWT token for authentication
 */
async function getAuthToken(): Promise<string> {
  const { data: { session }, error } = await supabase.auth.getSession()

  if (error) {
    console.error('Auth error:', error)
    throw new Error('Authentication error. Please try logging in again.')
  }

  if (!session) {
    throw new Error('Not authenticated. Please log in.')
  }

  return session.access_token
}

/**
 * Make an authenticated API request to the backend
 * OPTIMIZED: Added timeout handling to prevent hanging
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken()

  const url = `${BACKEND_URL}${endpoint}`

  // Create an AbortController for timeout
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const errorMessage = errorData.detail || `API error: ${response.status} ${response.statusText}`
      throw new Error(errorMessage)
    }

    return response.json()
  } catch (error: any) {
    clearTimeout(timeoutId)

    // Handle timeout errors
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. The backend may be slow or unreachable.')
    }

    throw error
  }
}

// =============================================
// API FUNCTIONS
// =============================================

/**
 * Get all commits that reference a specific clip
 */
export async function listClipCommits(clipId: string): Promise<ClipCommitsResponse> {
  return apiRequest<ClipCommitsResponse>(`/api/clips/${clipId}/commits`)
}

/**
 * Assign clips to a team member
 */
export async function assignClips(
  projectId: string,
  assignRequest: ClipAssignRequest
): Promise<ClipResponse[]> {
  return apiRequest<ClipResponse[]>(`/api/projects/${projectId}/clips/assign`, {
    method: 'POST',
    body: JSON.stringify(assignRequest),
  })
}
